  import React from 'react';

interface ErrorCode {
  code: number;
  status: string;
  description: string;
  example?: any;
}

interface ErrorCodesTableProps {
  errorCodes: ErrorCode[];
}

const ErrorCodesTable: React.FC<ErrorCodesTableProps> = ({ errorCodes }) => (
  <div className="w-full mt-4 border border-slate-200 rounded-lg overflow-hidden">
    <table className="w-full table-auto divide-y divide-slate-200">
      <thead className="bg-slate-50">
        <tr>
          <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Code
          </th>
          <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Status
          </th>
          <th className="w-7/12 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Description
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-slate-200">
        {errorCodes.map((error) => (
          <tr key={error.code}>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                error.code >= 200 && error.code < 300 
                  ? 'bg-green-100 text-green-800' 
                  : error.code >= 400 && error.code < 500
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {error.code}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-700">
              {error.status}
            </td>
            <td className="px-6 py-4 text-sm text-slate-600">
              {error.description}
              {error.example && (
                <div className="mt-2 p-2 bg-slate-50 rounded text-xs font-mono">
                  {JSON.stringify(error.example, null, 2)}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ErrorHandling: React.FC = () => {
  // Error codes from the generateSlideCustomSlide function
  const commonErrorCodes: ErrorCode[] = [
    {
      code: 200,
      status: "OK",
      description: "Slide generation completed successfully. Returns the presentation URL.",
      example: {
        url: "https://docs.google.com/presentation/d/1yuU-3y9zZYjLET1QFr7XcrdGAzXGM0fU0pW71rxS7Uw/edit"
      }
    },
    {
      code: 400,
      status: "Bad Request",
      description: "Data payload is missing or invalid. The request body must include the required data parameter.",
      example: {
        error: "Data payload is required for this section."
      }
    },
    {
      code: 402,
      status: "Insufficient credits",
      description: "Insufficient credits to generate the requested document - occurs when organization's available credits are less than the required slide count.",
      example: {
        error: "Insufficient credits, please purchase more credits to generate the document."
      }
    },
    {
      code: 404,
      status: "Not Found - Organization Profile",
      description: "The specified organization ID doesn't exist in the system.",
      example: {
        error: "Organization profile not found."
      }
    },
    {
      code: 404,
      status: "Not Found - Document Creation Failed",
      description: "Failed to initialize new document when presentationID is provided but no existing document found.",
      example: {
        error: "Document Creation Failed"
      }
    },
    {
      code: 404,
      status: "Not Found - Document",
      description: "No user form data found for the given documentID or presentationID.",
      example: {
        error: "Document not found."
      }
    },
    {
      code: 500,
      status: "Internal Server Error - Color Extraction",
      description: "Failed to extract colors from logo during cover section processing.",
      example: {
        error: "Failed to extract colors from logo."
      }
    },
    {
      code: 500,
      status: "Internal Server Error - Final Sheet Operations",
      description: "Error during final sheet retrieval or update operations.",
      example: {
        error: "Final sheet operation failed"
      }
    },
    {
      code: 500,
      status: "Internal Server Error - General",
      description: "Any other unexpected error during slide generation process.",
      example: {
        error: "An unexpected error occurred while generating the slide"
      }
    }
  ];

  return (
    <section
      id="error-handling"
      className="mt-12 pt-8 border-t border-slate-200 scroll-mt-20"
    >
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
        Error Handling
      </h2>
      <p className="mt-4 text-slate-600">
      This guide explains the error codes returned by Zynth.ai's API and offers recommendations on how to resolve them. Proper error handling ensures your integration with Zynth is reliable and user-friendly.a
      </p>
      <ErrorCodesTable errorCodes={commonErrorCodes} />
    </section>
  );
};

export default ErrorHandling;